import React, { useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import {
    createTemplateCategoryRoute,
    updateTemplateCategoryRoute,
    uploadCategoryImageRoute,
} from '../../../api/routes';
import config from '../../../config';
import FormField from '../../../components/forms/FormField/FormField';
import TextArea from '../../../components/forms/TextArea/TextArea';
import ImageUploader, { UPLOADER_IMAGE_SIDE } from '../../../components/forms/ImageUpload/ImageUploader';
import '../SiteAdmin.scss';

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

const EditTemplateCategory = ({ category, isNew = false, onBack, onSaved }) => {
    const [name, setName] = useState(category.name || '');
    const [description, setDescription] = useState(category.description || '');
    const [categoryImage, setCategoryImage] = useState(category.categoryImage || '');
    const [isActive, setIsActive] = useState(category.isActive === true);
    const [saving, setSaving] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(category);

    const handleSave = async () => {
        if (!name.trim()) return toast.error('Name is required');

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await axios.post(
                    createTemplateCategoryRoute,
                    {
                        name: name.trim(),
                        description: description.trim(),
                        categoryImage: categoryImage.trim(),
                        isActive,
                    },
                    { withCredentials: true }
                );
            } else {
                res = await axios.put(
                    `${updateTemplateCategoryRoute}/${currentCategory.id}`,
                    {
                        name: name.trim(),
                        description: description.trim(),
                        categoryImage: categoryImage.trim(),
                        isActive,
                    },
                    { withCredentials: true }
                );
            }
            if (res.data.success) {
                toast.success(isNew ? 'Category created!' : 'Category saved');
                setCurrentCategory(res.data.category);
                onSaved(res.data.category);
            }
        } catch {
            toast.error(isNew ? 'Failed to create category' : 'Failed to save category');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="edit-template-view">
            <div className="edit-template-header">
                <span className="et-back-btn" onClick={onBack} title="Back">&lt;</span>
                <h5 className="edit-template-title">
                    {isNew ? 'Create Template Category' : 'Edit Template Category'}
                </h5>
            </div>

            <div className="edit-template-body">
                <div className="site-admin-panel edit-template-panel">
                    <div className="site-admin-panel-title">Category Details</div>

                    <ImageUploader
                        label="Category Image"
                        id={currentCategory.id}
                        currentImg={toDisplayUrl(categoryImage)}
                        uploadImmediately={true}
                        endPoint={uploadCategoryImageRoute}
                        uploadBtnLabel="Upload Image"
                        showRemoveOption={false}
                        previewSide={UPLOADER_IMAGE_SIDE.right}
                        previewWidth={160}
                        previewHeight={90}
                        uploadedCallback={(res) => {
                            if (res.data.url) setCategoryImage(res.data.url);
                        }}
                        uploadErrorCallback={() => toast.error('Image upload failed')}
                    />

                    <FormField
                        showLabel={true}
                        label="Name"
                        type="text"
                        id="etc-name"
                        placeholder="Category name"
                        value={name}
                        setFunc={setName}
                    />

                    <TextArea
                        label="Description"
                        id="etc-description"
                        placeholder="Category description"
                        value={description}
                        setFunc={setDescription}
                        rows={4}
                    />

                    <div className="et-field">
                        <div className="sa-toggle-row">
                            <span className="et-label">Active</span>
                            <div
                                className={`tm-flag-toggle${isActive ? ' on' : ''}`}
                                onClick={() => setIsActive(v => !v)}
                                style={{ cursor: 'pointer' }}
                                title={isActive ? 'Click to deactivate' : 'Click to activate'}
                            />
                        </div>
                        <p style={{ fontSize: '0.75rem', color: 'var(--label-color)', margin: '0.25rem 0 0' }}>
                            {isActive
                                ? 'Category is visible to users'
                                : 'Category is hidden from users'}
                        </p>
                    </div>

                    <div className="et-actions">
                        <div
                            className={`standard-button button-active sa-save-btn${saving ? ' button-inactive' : ''}`}
                            onClick={saving ? undefined : handleSave}
                        >
                            {saving ? 'Saving…' : isNew ? 'Create Category' : 'Save Changes'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditTemplateCategory;
